package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.window
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.EventTarget
import org.w3c.dom.events.MouseEvent

/**
 * Captures the modality of the current browser activation sequence.
 *
 * Focus identity cannot answer this: the same Assist button can be clicked by a pointer or
 * activated with Enter/Space. A captured click classifies the activation at the event which owns
 * it, while the same-task pointer target keeps synthetic browser tests faithful. Ambient state is
 * discarded after that event (and abandoned pointerdowns expire at the task boundary), so one
 * action cannot leak its motion policy into the next.
 */
internal class EditorActivationOriginTracker {
    private var explicit: EditorUtilityActionOrigin? = null
    private var ambient: EditorUtilityActionOrigin? = null

    /** Records a caller-owned origin which must win over ambient browser inference. */
    fun record(origin: EditorUtilityActionOrigin) {
        explicit = origin
    }

    fun recordAmbient(origin: EditorUtilityActionOrigin) {
        ambient = origin
    }

    fun consume(): EditorUtilityActionOrigin {
        val current = explicit ?: ambient ?: EditorUtilityActionOrigin.KEYBOARD
        explicit = null
        ambient = null
        return current
    }

    fun discardAmbient() {
        ambient = null
    }

    fun discard() {
        explicit = null
        ambient = null
    }
}

@Composable
internal fun EditorActivationOriginEffect(tracker: EditorActivationOriginTracker) {
    DisposableEffect(tracker) {
        var syntheticPointerTarget: EventTarget? = null
        var pointerExpiry: Int? = null
        var keyboardExpiry: Int? = null

        val pointerListener = EventListener { event ->
            pointerExpiry?.let(window::clearTimeout)
            val target = event.target
            syntheticPointerTarget = target
            pointerExpiry = window.setTimeout({
                if (syntheticPointerTarget === target) syntheticPointerTarget = null
                pointerExpiry = null
            }, 0)
        }
        val keyboardListener = EventListener {
            keyboardExpiry?.let(window::clearTimeout)
            tracker.recordAmbient(EditorUtilityActionOrigin.KEYBOARD)
            keyboardExpiry = window.setTimeout({
                tracker.discardAmbient()
                keyboardExpiry = null
            }, 0)
        }
        val clickCaptureListener = EventListener { event ->
            val mouseEvent = event as? MouseEvent
            val pointerActivation = mouseEvent?.detail?.let { it > 0 } == true ||
                syntheticPointerTarget === event.target
            tracker.recordAmbient(
                if (pointerActivation) {
                    EditorUtilityActionOrigin.POINTER
                } else {
                    EditorUtilityActionOrigin.KEYBOARD
                },
            )
            pointerExpiry?.let(window::clearTimeout)
            pointerExpiry = null
            syntheticPointerTarget = null
        }
        val clickBubbleListener = EventListener { tracker.discardAmbient() }
        window.addEventListener("pointerdown", pointerListener, true)
        window.addEventListener("keydown", keyboardListener, true)
        window.addEventListener("click", clickCaptureListener, true)
        window.addEventListener("click", clickBubbleListener)
        onDispose {
            window.removeEventListener("pointerdown", pointerListener, true)
            window.removeEventListener("keydown", keyboardListener, true)
            window.removeEventListener("click", clickCaptureListener, true)
            window.removeEventListener("click", clickBubbleListener)
            pointerExpiry?.let(window::clearTimeout)
            keyboardExpiry?.let(window::clearTimeout)
            tracker.discard()
        }
    }
}
